import { useMobile } from '../hooks/useMobile';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import DealStageSelector from '../components/DealStageSelector';
import { usePermissions } from '../hooks/usePermissions';

const token = () => localStorage.getItem('token');

export default function DealEditPage() {
  const mobile = useMobile();
  const { dealId }  = useParams();
  const navigate    = useNavigate();
  const perms       = usePermissions();

  const [deal, setDeal]               = useState(null);
  const [introducers, setIntroducers] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');
  const [changingIntroducer, setChangingIntroducer] = useState(false);
  const [form, setForm]               = useState({
    client_name:    '',
    client_address: '',
    client_phone:   '',
    client_email:   '',
    client_notes:     '',
    product_type:     '',
    mortgage_end_date: '',
    introducer_id:  '',
    deal_stage_id:  null,   // RAG stage template FK
    lender_proc_fee: '',
    client_fee:      '',
  });

  useEffect(() => {
    if (!perms.canEditDeal && !perms.isPlatformOwner) {
      navigate(`/deal/${dealId}`);
      return;
    }
    loadDeal();
    loadIntroducers();
  }, [dealId]);

  const loadDeal = async () => {
    try {
      const res = await fetch(`/api/deals/${dealId}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error('Not found');
      const data = await res.json();
      setDeal(data);
      setForm({
        client_name:    data.client_name    || '',
        client_address: data.client_address || '',
        client_phone:   data.client_phone   || '',
        client_email:   data.client_email   || '',
        client_notes:      data.client_notes      || '',
        product_type:      data.product_type      || '',
        mortgage_end_date: data.mortgage_end_date
          ? new Date(data.mortgage_end_date).toISOString().slice(0, 10)
          : '',
        introducer_id:  data.introducer_user_id || '',
        deal_stage_id:  data.deal_stage_id  || null,
        lender_proc_fee: data.lender_proc_fee ?? '',
        client_fee:      data.client_fee ?? '',
      });
    } catch {
      setError('Failed to load deal.');
    } finally {
      setLoading(false);
    }
  };

  const loadIntroducers = async () => {
    try {
      const res = await fetch('/api/introducers', {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      setIntroducers(data.introducers || []);
    } catch { /* non-fatal */ }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!form.client_name || !form.client_phone || !form.client_email) {
      setError('Name, phone and email are required.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`/api/deals/${dealId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setSuccess('Deal updated successfully.');
      setTimeout(() => navigate(`/deal/${dealId}`), 1200);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const s = {
    page:    { display: 'flex', minHeight: '100vh', background: '#f8f9fa' },
    main:    { flex: 1, marginLeft: mobile ? 0 : 70, padding: '32px 40px', width: mobile ? '100%' : 'calc(100% - 70px)', boxSizing: 'border-box' },
    card:    { background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: '28px 32px', maxWidth: 680 },
    label:   { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 },
    input:   { width: '100%', border: '1px solid #CBD5E0', borderRadius: 8, padding: '10px 14px', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
    section: { marginBottom: 24 },
    grid2:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
    sectionHead: { margin: '0 0 16px', fontSize: 15, color: '#0e1b2c', borderBottom: '1px solid #EDF2F7', paddingBottom: 10 },
  };

  if (loading) return (
    <div style={s.page}><Sidebar />
      <main style={s.main}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, color: '#718096' }}>Loading…</div>
      </main>
    </div>
  );

  return (
    <div style={s.page}>
      <Sidebar />
      <main style={s.main}>
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={() => navigate(`/deal/${dealId}`)}
            style={{ background: 'none', border: '1px solid #CBD5E0', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: '#718096' }}>
            ← Back
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, color: '#0e1b2c' }}>Edit Deal</h1>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#718096' }}>{deal?.client_name}</p>
          </div>
        </div>

        <div style={s.card}>
          <form onSubmit={handleSubmit}>

            {/* Introducer */}
            <div style={s.section}>
              <h3 style={s.sectionHead}>Introducer</h3>
              {!changingIntroducer ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    flex: 1, display: 'flex', alignItems: 'center', gap: 10,
                    background: '#f8fafc', border: '1px solid #E2E8F0',
                    borderRadius: 8, padding: '10px 14px',
                  }}>
                    <span style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: '#0e1b2c', color: '#10b981',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 14, flexShrink: 0,
                    }}>
                      {(deal?.introducer_name || 'I').charAt(0).toUpperCase()}
                    </span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#0e1b2c' }}>
                        {deal?.introducer_name || 'Unknown introducer'}
                      </div>
                      <div style={{ fontSize: 12, color: '#a0aec0' }}>Introducing agent</div>
                    </div>
                  </div>
                  <button type="button" onClick={() => setChangingIntroducer(true)}
                    style={{
                      background: 'none', border: '1px solid #CBD5E0', borderRadius: 8,
                      padding: '8px 14px', fontSize: 13, color: '#718096',
                      cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                    }}>
                    Change introducer
                  </button>
                </div>
              ) : (
                <div>
                  <label style={s.label}>Select new introducer</label>
                  <select name="introducer_id" value={form.introducer_id} onChange={handleChange} style={s.input}>
                    <option value="">— Select —</option>
                    {introducers.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                  <button type="button" onClick={() => { setChangingIntroducer(false); setForm(f => ({ ...f, introducer_id: deal?.introducer_user_id || '' })); }}
                    style={{ marginTop: 8, background: 'none', border: 'none', fontSize: 12, color: '#a0aec0', cursor: 'pointer', padding: 0 }}>
                    ✕ Cancel change
                  </button>
                </div>
              )}
            </div>

            {/* Client details */}
            <div style={s.section}>
              <h3 style={s.sectionHead}>Client Details</h3>
              <div style={{ ...s.grid2, marginBottom: 16 }}>
                <div>
                  <label style={s.label}>Full Name *</label>
                  <input name="client_name" value={form.client_name} onChange={handleChange} style={s.input} required />
                </div>
                <div>
                  <label style={s.label}>Address</label>
                  <input name="client_address" value={form.client_address} onChange={handleChange} style={s.input} />
                </div>
              </div>
              <div style={{ ...s.grid2, marginBottom: 16 }}>
                <div>
                  <label style={s.label}>Phone *</label>
                  <input name="client_phone" value={form.client_phone} onChange={handleChange} style={s.input} required />
                </div>
                <div>
                  <label style={s.label}>Email *</label>
                  <input name="client_email" type="email" value={form.client_email} onChange={handleChange} style={s.input} required />
                </div>
              </div>
              <div>
                <label style={s.label}>Notes</label>
                <textarea name="client_notes" value={form.client_notes} onChange={handleChange}
                  style={{ ...s.input, resize: 'vertical' }} rows={3} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
                  <div>
                    <label style={s.label}>Product Type</label>
                    <select name="product_type" value={form.product_type} onChange={handleChange} style={s.input}>
                      <option value="">— Select —</option>
                      <option value="residential_purchase">Residential Purchase</option>
                      <option value="residential_remortgage">Residential Remortgage</option>
                      <option value="buy_to_let_purchase">Buy to Let Purchase</option>
                      <option value="buy_to_let_remortgage">Buy to Let Remortgage</option>
                      <option value="commercial">Commercial</option>
                      <option value="bridging">Bridging</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={s.label}>Mortgage End Date</label>
                    <input
                      type="date"
                      name="mortgage_end_date"
                      value={form.mortgage_end_date}
                      onChange={handleChange}
                      style={s.input}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Deal value — what the broker earns on this deal */}
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', padding: 24, marginTop: 20 }}>
              <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: '#0e1b2c' }}>Deal Value</h2>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: '#718096' }}>
                What you earn on this deal. If the introducer is on a percentage split, their commission is recalculated automatically from these figures.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div>
                  <label style={s.label}>Lender Proc Fee (£)</label>
                  <input type="number" step="0.01" min="0" name="lender_proc_fee" value={form.lender_proc_fee}
                    onChange={handleChange} style={s.input} placeholder="e.g. 1000.00" />
                </div>
                <div>
                  <label style={s.label}>Client Fee (£)</label>
                  <input type="number" step="0.01" min="0" name="client_fee" value={form.client_fee}
                    onChange={handleChange} style={s.input} placeholder="e.g. 495.00" />
                </div>
                <div>
                  <label style={s.label}>Total Deal Value</label>
                  <div style={{ ...s.input, background: '#f8fafc', display: 'flex', alignItems: 'center', fontWeight: 700, color: '#0e1b2c' }}>
                    £{((parseFloat(form.lender_proc_fee) || 0) + (parseFloat(form.client_fee) || 0)).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            {/* RAG Deal Progress Stage — NEW */}
            <div style={s.section}>
              <h3 style={s.sectionHead}>Deal Progress Stage</h3>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#718096' }}>
                Set the current progress stage for this deal. Introducers can see this on their dashboard.
                Moving to a stage marked "Completed" or "Lost/Withdrawn" also updates this deal's
                commission tracking status automatically — there's no separate control for that.
              </p>
              <DealStageSelector
                value={form.deal_stage_id}
                onChange={(id) => setForm(f => ({ ...f, deal_stage_id: id }))}
              />
              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#a0aec0' }}>
                Manage your stages at{' '}
                <a href="/stages" style={{ color: '#10b981', textDecoration: 'none' }}>Settings → Deal Stages</a>
              </p>
            </div>

            {error   && <div style={{ background: '#fff5f5', border: '1px solid #fc8181', borderRadius: 8, padding: '10px 14px', color: '#c53030', marginBottom: 16, fontSize: 14 }}>⚠️ {error}</div>}
            {success && <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 8, padding: '10px 14px', color: '#065f46', marginBottom: 16, fontSize: 14 }}>✓ {success}</div>}

            <div style={{ display: 'flex', gap: 12 }}>
              <button type="submit" disabled={saving}
                style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '11px 28px', fontWeight: 600, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button type="button" onClick={() => navigate(`/deal/${dealId}`)}
                style={{ background: '#fff', border: '1px solid #CBD5E0', borderRadius: 8, padding: '11px 20px', fontSize: 14, cursor: 'pointer', color: '#718096' }}>
                Cancel
              </button>
            </div>

          </form>
        </div>
      </main>
    </div>
  );
}
